import { Modal } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";

import { useParams } from "react-router-dom";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { Button } from "../shared/Button/Button";

import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";

import { PatientInsuranceInfo } from "../Profile/helpers";
import {
  ProfileModalTitle,
  ProfileModalTitleWrapper,
  ProfileModalButtons,
} from "../shared/Modal/Modal.styles";
import ProfileInsuranceUpload from "../Profile/ProfileInsuranceUpload";
import {
  UpfrontModalContentWrapper,
  UpfrontPaymentButtonRow,
  UpfrontPaymentFinancialModalWrapper,
  UpfrontPaymentFinancialReceipt,
  UpfrontPaymentFinancialReceiptTitle,
  UpfrontPaymentSecondaryReceiptTitle,
} from "./UpfrontPayment.styles";
import {
  SubmitNewUpfrontInsurance,
  Bill,
} from "./Services/UpfrontPaymentService";
import { GreenFullCheckIcon } from "../../assets/iconComponents/GreenFullCheckIcon";
import { InsuranceUploadType } from "../Insurance/types";

const getInsuranceTypeKey = (insuranceType?: InsuranceUploadType) => {
  if (insuranceType === InsuranceUploadType.primary) {
    return "insurance";
  }
  if (insuranceType === InsuranceUploadType.secondary) {
    return "secondaryInsurance";
  }
  if (insuranceType === InsuranceUploadType.medicaid_or_hmo) {
    return "medicaidOrHmoInsurance";
  }
  return "insurance";
};

const getInsuranceTypeTitleKey = (insuranceType?: InsuranceUploadType) => {
  if (insuranceType === InsuranceUploadType.primary) {
    return "insurance";
  }
  if (insuranceType === InsuranceUploadType.secondary) {
    return "secondaryInsurance";
  }
  if (insuranceType === InsuranceUploadType.medicaid_or_hmo) {
    return "medicaidOrHmoInsurance";
  }
  return "insurance";
};

export const UpfrontInsuranceUploadModal: React.FC<{
  insuranceType: InsuranceUploadType;
  insuranceUploadModalOpen: boolean;
  setInsuranceUploadModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUpfrontBill: React.Dispatch<React.SetStateAction<Bill | undefined>>;
  setHomeScreen: () => void;
}> = ({
  insuranceType,
  insuranceUploadModalOpen,
  setInsuranceUploadModalOpen,
  setUpfrontBill,
  setHomeScreen,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const insuranceTypeKey = getInsuranceTypeKey(insuranceType);
  const { billingPK, adminID } = useParams<{
    billingPK: string;
    adminID?: string;
  }>();
  const [loading, setLoading] = useState(false);

  const [secondaryInsuranceResponse, setSecondaryInsuranceResponse] =
    useState<boolean>(false);

  const [patientInsuranceInfo, setPatientInsuranceInfo] =
    React.useState<PatientInsuranceInfo>({
      billToInsuranceType: "Upload",
      insuranceCompanyName: "",
      groupId: "",
      memberId: "",
      policyOwnerName: "",
      policyOwnerDob: "",
      relationshipToPolicyOwnerSelf: true,
      relationshipToPolicyOwnerDependent: false,
      relationshipToPolicyOwnerOther: false,
      insuranceFileUrls: {},
      checkError: false,
    });

  return (
    <Modal
      open={insuranceUploadModalOpen}
      onClose={() => {
        setInsuranceUploadModalOpen(false);
      }}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <>
        {secondaryInsuranceResponse ? (
          <UpfrontPaymentFinancialModalWrapper
            deviceType={deviceType}
            allowScroll={false}
            receipt
          >
            <UpfrontPaymentFinancialReceipt deviceType={deviceType}>
              <GreenFullCheckIcon />
              <UpfrontPaymentFinancialReceiptTitle>
                {t("upfrontPayment.insuranceUpload.thankYou", {
                  insuranceType: t(
                    `upfrontPayment.insuranceUpload.types.${insuranceTypeKey}`,
                  ),
                })}
              </UpfrontPaymentFinancialReceiptTitle>
              <UpfrontPaymentSecondaryReceiptTitle>
                {t("upfrontPayment.insuranceUpload.reviewNotice", {
                  insuranceType: t(
                    `upfrontPayment.insuranceUpload.types.${insuranceTypeKey}`,
                  ),
                })}
              </UpfrontPaymentSecondaryReceiptTitle>
              {insuranceType === InsuranceUploadType.secondary && (
                <UpfrontPaymentSecondaryReceiptTitle>
                  {t("upfrontPayment.insuranceUpload.secondaryMedicaidNote")}
                </UpfrontPaymentSecondaryReceiptTitle>
              )}
              <UpfrontPaymentButtonRow>
                <Button
                  label={t("upfrontPayment.actions.done")}
                  onClick={() => setInsuranceUploadModalOpen(false)}
                  size="medium"
                  backgroundColor="greenDark"
                />
              </UpfrontPaymentButtonRow>
            </UpfrontPaymentFinancialReceipt>
          </UpfrontPaymentFinancialModalWrapper>
        ) : (
          <UpfrontPaymentFinancialModalWrapper
            deviceType={deviceType}
            allowScroll
          >
            <ProfileModalTitle deviceType={deviceType}>
              <ProfileModalTitleWrapper deviceType={deviceType}>
                <div>
                  {t(
                    `upfrontPayment.insuranceUpload.typeTitles.${getInsuranceTypeTitleKey(
                      insuranceType,
                    )}`,
                  )}
                </div>
                <CloseIcon
                  onClick={() => {
                    setInsuranceUploadModalOpen(false);
                  }}
                />
              </ProfileModalTitleWrapper>
            </ProfileModalTitle>
            <UpfrontModalContentWrapper>
              <ProfileInsuranceUpload
                patientInsuranceInfo={patientInsuranceInfo}
                setPatientInsuranceInfo={setPatientInsuranceInfo}
                insuranceType={insuranceType}
              />
            </UpfrontModalContentWrapper>

            {loading ? (
              <SpinnerLoading />
            ) : (
              <ProfileModalTitle deviceType={deviceType}>
                <ProfileModalButtons deviceType={deviceType}>
                  <Button
                    className="upfront-cancel-button"
                    label={t("upfrontPayment.actions.close")}
                    onClick={() => {
                      setInsuranceUploadModalOpen(false);
                    }}
                    size="medium"
                    reverse
                    textColor="greyMedium"
                    backgroundColor="greyMedium"
                  />
                  <Button
                    label={t("upfrontPayment.actions.submit")}
                    className="upfront-submit-button"
                    disabled={
                      Object.keys(patientInsuranceInfo.insuranceFileUrls)
                        .length < 2
                    }
                    onClick={async () => {
                      setLoading(true);
                      const response = await SubmitNewUpfrontInsurance(
                        Object.values(
                          patientInsuranceInfo.insuranceFileUrls,
                        ).map((file) => file.s3Url),
                        billingPK,
                        insuranceType,
                        adminID,
                      );
                      setLoading(false);
                      if (response?.ok) {
                        const secondaryInsuranceJson = await response.json();
                        setUpfrontBill(secondaryInsuranceJson);
                        setSecondaryInsuranceResponse(true);
                        setHomeScreen();
                      }
                    }}
                    size="medium"
                    backgroundColor="greenDark"
                  />
                </ProfileModalButtons>
              </ProfileModalTitle>
            )}
          </UpfrontPaymentFinancialModalWrapper>
        )}
      </>
    </Modal>
  );
};
